import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { fetchCurrentPlan } from "../../../../shared/hooks/useFetchCurrentPlan";
import API_URLS from "../../Utils/axr";
import { errorConditionGTM, upcomingPaymentGTM } from "../../Utils/googleTags";
import store from "../store";
import DwLogutil from "../../Utils/DwLogutil";
import { EC_DW_LOGGING_STEPS } from "../../../../shared/utils/Constants";
import useBrightspeedUser from "../../hooks/useUser";
import { shouldStopApiRetry, logApiRetryCapDwLog } from "../../Utils/utils";

type nextPaymentDetailsInitialStateType = {
  nextPaymentDetails: any;
  loading: boolean;
  error: any;
  fetched: boolean;  // upcoming-payments API fetched flag
  retryCount: number;  // Track number of API attempts
};
const initialState: nextPaymentDetailsInitialStateType = {
  nextPaymentDetails: null,
  loading: false,
  error: null,
  fetched: false,  // upcoming-payments API fetched flag
  retryCount: 0,  // Track number of API attempts
};

export const getNextPaymentDetails = createAsyncThunk(
  "nextPayment/details",
  async (billingAccountNumber: string) => {
     const reduxStore = store.getState();
      const headers = reduxStore?.brightspeedToken?.headers;
      const {sessionId,companyName} = reduxStore?.user;

      const accountType =reduxStore?.user?.data?.billingType?.toLowerCase() === "postpaid" ? "POSTPAID" : "PREPAID"
      const isPostpaidFiber = reduxStore?.brightSpeedUserUtils?.isPostPaidFiberUser;
    try {
     
      const params = {
        [API_URLS?.upcomingPayment_QA_Url]: billingAccountNumber,
      };

      const response = await fetchCurrentPlan(params, headers);
      if(response?.data?.dueDate && response?.data?.nextPayment && reduxStore?.nextPaymentDetails?.nextPaymentDetails === null) {
        DwLogutil({stepName: EC_DW_LOGGING_STEPS.upcoming_payment_success})
        upcomingPaymentGTM("upcoming_payment_success",companyName || headers?.siteId,"",accountType,billingAccountNumber)
      } if(response?.data?.dueDate === null && response?.data?.nextPayment === null && reduxStore?.nextPaymentDetails?.error === null) {
        DwLogutil({stepName: EC_DW_LOGGING_STEPS.upcoming_payment_failed, error: "Response data is empty"});
        upcomingPaymentGTM("upcoming_payment_failed",companyName || headers?.siteId,"Response data is empty",accountType,billingAccountNumber)
      }
      if(response?.data?.dueDate === null || response?.data?.dueDate === "" || response?.data?.nextPayment === null || response?.data?.nextPayment === "") {
         DwLogutil({stepName: EC_DW_LOGGING_STEPS.upcoming_payment_API_Response, error: "nextpayment or dueDate is empty or null "});
      }
      if(!response?.data?.invoiceGenerated){
        const stepName = isPostpaidFiber ? EC_DW_LOGGING_STEPS?.ppf_bill_pending :`${EC_DW_LOGGING_STEPS.user_invoice_not_generated}`;
        isPostpaidFiber && DwLogutil({ headers, sessionId, companyName: companyName, stepName });
      }
      return response.data;
    } catch (error) {
      DwLogutil({stepName: EC_DW_LOGGING_STEPS.upcoming_payment_failed, error: "somthing went wrong while fetching the API details"});
       upcomingPaymentGTM("upcoming_payment_failed",companyName || headers?.siteId, error ||
        "somthing went wrong while fetching the API details",accountType,billingAccountNumber)
      throw error;
    }
  }
);

const nextPaymentDetailSlice = createSlice({
  name: "nextPayment/Details",
  initialState,
  reducers: {
    clearNextPaymentDetailSlice: (state) => {
      return initialState;
    },
    nextPaymentDetailSliceSetInitialState: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(getNextPaymentDetails.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getNextPaymentDetails.fulfilled, (state, action) => {
      state.nextPaymentDetails = action.payload;
      state.loading = false;
      state.error = null;
      state.retryCount = 0;  // Reset retry count on success
      state.fetched = true;  // Mark as fetched on success
    });
    builder.addCase(getNextPaymentDetails.rejected, (state, action) => {
      errorConditionGTM(
        "BRSPD_Fiber_EC_Flow",
        "next payment",
        action.error?.message,
        action?.type
      );
      state.nextPaymentDetails = null;
      state.loading = false;
      state.error = action.error;
      state.retryCount += 1;  // Increment retry count on failure
      DwLogutil({stepName: `upcoming-payments API failed ${state.retryCount} time(s)`, error: `${action.error?.message}`});
      if (shouldStopApiRetry(state.retryCount)) {
        state.fetched = true;  // Block further retries after max failures
        logApiRetryCapDwLog(
          EC_DW_LOGGING_STEPS.upcoming_payment_API_retry_cap,
          action.error?.message
        );
      }
    });
  },
});

export const {
  clearNextPaymentDetailSlice,
  nextPaymentDetailSliceSetInitialState,
} = nextPaymentDetailSlice.actions;
export default nextPaymentDetailSlice.reducer;
